import { Toaster } from "@/shared/ui/toaster";
import { Toaster as Sonner } from "@/shared/ui/sonner";
import { TooltipProvider } from "@/shared/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useParams } from "react-router-dom";
import { APP_BASE_PATH } from "@/shared/lib/appBasePath";
import { AuthProvider } from "@/shared/hooks/useAuth";
import { ClientSelectionProvider } from "@/features/projects/contexts/ClientSelectionContext";
import Index from "./features/auth/pages/Index";
import Login from "./features/auth/pages/Login";
import Sandbox from "./features/auth/pages/Sandbox";
import InstallApp from "./features/auth/pages/InstallApp";
import { ClientLayoutWithSidebar } from "./features/projects/components/ClientLayoutWithSidebar";
import { ProtectedRoute } from "./features/auth/components/ProtectedRoute";
import { ErrorBoundaryWithNetwork } from "@/shared/components/ErrorBoundaryWithNetwork";
import { DownloadManagerProvider } from "@/features/exports/services/DownloadManagerContext";
import { DownloadTray } from "@/features/exports/components/DownloadTray";
import { PWAUpdatePrompt } from "@/shared/components/PWAUpdatePrompt";
import { AnalyticsPageViews } from "@/shared/integrations/firebase/AnalyticsPageViews";

// Client imports
import ClientDashboardAgent from "./features/dashboard/pages/ClientDashboardAgent";
import ClientProperties from "./features/properties/pages/ClientProperties";
import ClientAssets from "./features/assets/pages/ClientAssets";
import ClientPropertyDetail from "./features/properties/pages/ClientPropertyDetail";
import ClientAssetDetail from "./features/assets/pages/ClientAssetDetail";
import ClientDeveloper from "./features/projects/pages/ClientDeveloper";

import SiteSurveyEditor from "./features/site-survey-editor/pages/SiteSurveyEditor";

import ClientExportsPage from "./features/exports/pages/ClientExportsPage";
import LearningPage from "./features/learning/pages/LearningPage";

import NotFound from "./shared/components/NotFound";

const queryClient = new QueryClient();

/**
 * Legacy redirect: /owner/client/:clientId/* → /*
 * Strips the /owner/client/{uuid} prefix and preserves the rest of the path.
 */
function LegacyOwnerRedirect() {
  const { "*": rest } = useParams();
  const target = rest ? `/${rest}` : "/dashboard";
  return <Navigate to={target} replace />;
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <ClientSelectionProvider>
        <DownloadManagerProvider>
          <TooltipProvider>
            <Toaster />
            <Sonner />
            <PWAUpdatePrompt />
            <DownloadTray />
            <ErrorBoundaryWithNetwork>
              <BrowserRouter
                basename={APP_BASE_PATH}
                future={{ v7_startTransition: true, v7_relativeSplatPath: true }}
              >
              <AnalyticsPageViews />
              <Routes>
                <Route path="/" element={<Index />} />
                <Route path="/signin" element={<Login />} />
                <Route path="/demo" element={<Sandbox />} />
                <Route path="/install" element={<InstallApp />} />

                {/* Independent Site Survey Editor */}
                <Route
                  path="/site-surveys/*"
                  element={
                    <ProtectedRoute>
                      <SiteSurveyEditor />
                    </ProtectedRoute>
                  }
                />

                {/* Legacy redirects: /owner/client/{uuid}/* → /* */}
                <Route path="/owner" element={<Navigate to="/dashboard" replace />} />
                <Route path="/owner/client" element={<Navigate to="/dashboard" replace />} />
                <Route path="/owner/client/:clientId/*" element={<LegacyOwnerRedirect />} />
                <Route path="/owner/client/:clientId" element={<Navigate to="/dashboard" replace />} />

                {/* Main app routes — clientId resolved automatically from context */}
                <Route
                  element={
                    <ProtectedRoute>
                      <ClientLayoutWithSidebar />
                    </ProtectedRoute>
                  }
                >
                  <Route path="dashboard" element={<ClientDashboardAgent />} />
                  <Route path="properties" element={<ClientProperties />} />
                  <Route path="assets" element={<ClientAssets />} />
                  <Route path="developer" element={<ClientDeveloper />} />
                  <Route path="exports" element={<ClientExportsPage />} />
                  <Route path="learning" element={<LearningPage />} />

                  <Route path="property/:propertyId" element={<ClientPropertyDetail />} />
                  <Route path="property/:propertyId/files" element={<ClientPropertyDetail />} />
                  <Route path="property/:propertyId/reports" element={<ClientPropertyDetail />} />
                  <Route path="property/:propertyId/vr" element={<ClientPropertyDetail />} />
                  <Route path="property/:propertyId/utility" element={<ClientPropertyDetail />} />
                  <Route path="property/:propertyId/drawings" element={<ClientPropertyDetail />} />
                  <Route path="property/:propertyId/3d" element={<ClientPropertyDetail />} />
                  <Route path="property/:propertyId/exports" element={<ClientPropertyDetail />} />

                  {/* Redirect from plural /properties/:propertyId to singular /property/:propertyId */}
                  <Route path="properties/:propertyId" element={<PropertyRedirectToSingular />} />
                  <Route path="properties/:propertyId/*" element={<PropertyRedirectToSingular />} />

                  <Route path="asset/:assetId" element={<ClientAssetDetail />} />
                  <Route path="properties/:propertyId/assets/:assetId" element={<ClientAssetDetail />} />
                  <Route path="property/:propertyId/assets/:assetId" element={<ClientAssetDetail />} />

                  {/* Canonical facility-project link: /facility/{projectID} (ref_id).
                      The :propertyId param resolves by ref_id or Supabase id in
                      ClientPropertyDetail. Static routes above take precedence, so
                      /dashboard, /properties, etc. are unaffected. */}
                  <Route path=":propertyId" element={<ClientPropertyDetail />} />
                </Route>

                <Route path="*" element={<NotFound />} />
              </Routes>
              </BrowserRouter>
            </ErrorBoundaryWithNetwork>
          </TooltipProvider>
        </DownloadManagerProvider>
        </ClientSelectionProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
}

function PropertyRedirectToSingular() {
  const { propertyId, "*": rest } = useParams();
  const path = rest
    ? `/property/${propertyId}/${rest}`
    : `/property/${propertyId}`;
  return <Navigate to={path} replace />;
}

export default App;

